import dayjs from 'dayjs';

import {
	useBasicTicketCost,
	useDTicketStartEndDate,
	useDTicketSubscriptionOffering,
} from '@mopla/business-logic';
import {
	ESubscriptionStatus,
	ISubscriptionTicket,
	TSubscription,
} from '@mopla/data-models';
import {
	MplDeutschlandTicketCardActive,
	MplDeutschlandTicketCardInvalid,
	MplDeutschlandTicketCardPaused,
	MplDeutschlandTicketCardPending,
	MplDeutschlandTicketCardPreOrdered,
} from '@mopla/ui';
import { formatPriceCompact } from '@mopla/utils';

import { useDTicketFirstAndLastName } from '../hooks/useDTicketFirstAndLastName';
import { getIsJobticket } from '../utils/utils';

interface IDTicketCardProps {
	subscription: TSubscription;
	ticket?: ISubscriptionTicket;
}

export const DTicketCard = ({ subscription, ticket }: IDTicketCardProps) => {
	const { subscriptionOffering } = useDTicketSubscriptionOffering();

	const { newPrice } = useBasicTicketCost(
		ticket?.validFrom || subscription?.subscriptionStart
	);

	const formattedPrice = formatPriceCompact(
		subscriptionOffering?.price ?? newPrice
	);

	const isJobticket = getIsJobticket(subscription?.type);

	const { firstName, lastName } = useDTicketFirstAndLastName({
		isJobticket,
		ticket,
	});

	const isTicketInvalid = ticket?.status === 'INVALID';
	const isTicketValid = ticket?.status === 'VALID';
	const isSubscriptionPaused =
		subscription?.status === ESubscriptionStatus.PAUSED;
	const isSubscriptionPreordered =
		subscription?.status === ESubscriptionStatus.PREORDERED;
	const isSubscriptionInvalid =
		subscription?.status === ESubscriptionStatus.INVALID;

	const { startDate, endDate } = useDTicketStartEndDate({
		currentTicket: ticket,
	});

	/** A ticket with status: VALID, whose validity period has not started yet is pre-ordered
	 * (e.g. next month's ticket that is already generated on the last day of the current month).
	 */
	const isTicketNotStartedYet = Boolean(
		ticket && isTicketValid && dayjs().isBefore(dayjs(ticket.validFrom))
	);

	// Invalid ticket or invalid subscription without ticket
	if (isTicketInvalid || isSubscriptionInvalid) {
		return (
			<MplDeutschlandTicketCardInvalid
				firstName={firstName}
				lastName={lastName}
				formattedPrice={formattedPrice}
				ticketId={ticket?.ticketId || ''}
			/>
		);
	}

	// Paused subscription
	if (isSubscriptionPaused) {
		return (
			<MplDeutschlandTicketCardPaused
				firstName={firstName}
				lastName={lastName}
				formattedPrice={formattedPrice}
				validFrom={dayjs(subscription?.pauseStart)}
				validUntil={dayjs(subscription?.pauseEnd)}
			/>
		);
	}

	// Preordered subscription without ticket
	if (!ticket && isSubscriptionPreordered) {
		return (
			<MplDeutschlandTicketCardPreOrdered
				firstName={firstName}
				lastName={lastName}
				formattedPrice={formattedPrice}
				validFrom={startDate}
				validUntil={endDate}
			/>
		);
	}

	// Ticket for an upcoming validity period - not usable yet, so pre-ordered
	if (isTicketNotStartedYet) {
		return (
			<MplDeutschlandTicketCardPreOrdered
				firstName={firstName}
				lastName={lastName}
				formattedPrice={formattedPrice}
				validFrom={startDate}
				validUntil={endDate.subtract(1, 'day')}
			/>
		);
	}

	/** The only "in processing" case: the ticket's validity has already started
	 * but the aztec code is not generated yet, so the ticket cannot be shown.
	 * This means Stripe is handling the payment */
	if (isTicketValid && !ticket.aztecCode) {
		return (
			<MplDeutschlandTicketCardPending
				firstName={firstName}
				lastName={lastName}
				formattedPrice={formattedPrice}
			/>
		);
	}

	// Active valid ticket
	if (isTicketValid) {
		return (
			<MplDeutschlandTicketCardActive
				id={ticket.id}
				firstName={firstName}
				lastName={lastName}
				formattedPrice={formattedPrice}
				ticketId={ticket.ticketId || ''}
				ticketAztecCode={ticket.aztecCode || ''}
				validFrom={startDate}
				validUntil={endDate.subtract(1, 'day')}
				timeLeft={endDate}
			/>
		);
	}

	return null;
};
